import {NotFoundError} from '@packages/utils';
import type {GetServerSideProps, GetServerSidePropsResult} from 'next';
import {z} from 'zod';
import {
  type GetUserDataForAnalyticsResponseType,
  getUserDataForAnalytics,
} from '../../../core/repositories/usersMeRepository';
import {
  type GetWebSeminarsEventsResponse,
  getWebSeminarsEvents,
} from '../../../core/repositories/webSeminarsRepository';
import {validateCookie} from '../../../core/usecases/auth/checkOauthTokenExistence';
import {WebSeminarsPage} from '../../../features/webSeminars/components/WebSeminarsPage';
import {withHandler} from '../../../server/token/withHandler';
import {ScriptForGA} from '../../../shared/components/ScriptForGA';
import {NoLeftSidebarLayout} from '../../../shared/components/layouts/NoLeftSidebarLayout';
import type {NextPageWithLayout} from '../../_app';

type Props = {
  webSeminarsEvents?: GetWebSeminarsEventsResponse;
  userDataForAnalytics?: GetUserDataForAnalyticsResponseType;
  isStudentVerificationRequired: boolean;
};

const QueryParameter = z.object({
  id: z.string(),
});

type QueryParameter = z.infer<typeof QueryParameter>;

export const getServerSideProps: GetServerSideProps = withHandler<
  Props,
  'withOptionalSession' | 'withQuery',
  QueryParameter
>(
  {auth: 'optional', queryValidator: QueryParameter},
  async (context): Promise<GetServerSidePropsResult<Props>> => {
    const {id} = context.validatedQuery;
    if (!id) {
      return {
        notFound: true,
      };
    }

    const webSeminarsEvents = await getWebSeminarsEvents({
      eventID: id,
    });
    if (!webSeminarsEvents.ok) {
      if (webSeminarsEvents.error === NotFoundError) {
        return {
          notFound: true,
        };
      }

      throw new Error(webSeminarsEvents.error);
    }

    const props: Props = {
      webSeminarsEvents: webSeminarsEvents.value,
      isStudentVerificationRequired: context.isStudentVerificationRequired,
    };

    const {cookies} = context.req;
    const {isValid} = validateCookie(cookies);

    if (!isValid) return {props};
    const userDataResponse = await getUserDataForAnalytics({
      cookies: context.req.cookies,
    });
    if (!userDataResponse.ok) {
      return {
        props,
      };
    }

    return {
      props: {
        ...props,
        userDataForAnalytics: userDataResponse.value,
      },
    };
  },
);

const Page: NextPageWithLayout<Props> = ({
  webSeminarsEvents,
  userDataForAnalytics,
  isStudentVerificationRequired,
}: Props) => {
  return (
    <>
      <WebSeminarsPage
        webSeminarsEvents={webSeminarsEvents}
        isStudentVerificationRequired={isStudentVerificationRequired}
      />
      <ScriptForGA userDataForAnalytics={userDataForAnalytics} />
    </>
  );
};

export default Page;

Page.getLayout = (page) => <NoLeftSidebarLayout>{page}</NoLeftSidebarLayout>;
